<template>
    <div>
        <portal to="page-header">
            {{ $t('Details') }} {{ $t('for') }} {{ player.username }}
        </portal>

        <portal to="actions">
            <div class="flex flex-row-reverse flex-wrap">
                <back-button
                    :route-name="'players.index'"
                >{{ $t('Players List') }}</back-button>
                <btn :type="'button'" @click="refresh">
                    <font-awesome-icon icon="sync" />
                </btn>
                <btn :type="'button'" class="mr-4" @click="unlockWithdrawals" v-if="withdrawalsLocked" :color="'red'">
                    Unlock Withdrawals
                </btn>
            </div>
        </portal>

        <div class="flex flex-wrap justify-start">
            <div
                v-if="player.type !== 'terminal'"
                class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/2"
            >
                <card>
                    <div id="playerQr">
                        <div class="text-xs text-center break-words w-full">{{ route('first.login', player.hash) }}</div>
                        <qrcode class="mt-4 mx-auto" :value="route('first.login', player.hash)" :options="{ width: 250 }"></qrcode>
                    </div>
                    <div class="mt-2 text-center block lg:hidden">
                        <btn
                            @click="share"
                            :type="'button'"
                            color="purple"
                            :aria-label="$t('Share')"
                            v-if="isAbleToShare()"
                            :title="$t('Share')">
                            <font-awesome-icon icon="share-alt" class="mr-2" />&nbsp;{{ $t('Share') }}
                        </btn>
                    </div>
                    <div class="mt-2 text-center block">
                        <btn
                            @click="printQr"
                            :type="'button'"
                            color="purple"
                            :aria-label="$t('Print')"
                            :title="$t('Print')">
                            <font-awesome-icon icon="print" class="mr-2" />&nbsp;{{ $t('Print') }}
                        </btn>
                    </div>
                </card>
            </div>
            <div
                :class="{
                'sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/2': player.type !== 'terminal',
                'sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full': player.type === 'terminal'
                }"
            >
                <card>
                    <div class="flex w-full">
                        <div class="px-4 py-2 w-1/2 sm:w-1/4">
                            <h4 class="font-normal text-gray-500 mt-2">{{ $t('Username') }}</h4>
                        </div>
                        <div class="px-4 py-4 w-1/2 sm:w-3/4">
                            <p class="">{{ player.username }}</p>
                        </div>
                    </div>
                    <div class="flex w-full">
                        <div class="px-4 py-2 w-1/2 sm:w-1/4">
                            <h4 class="font-normal text-gray-500 mt-2">{{ $t('Last Login') }}</h4>
                        </div>
                        <div class="px-4 py-4 w-1/2 sm:w-3/4">
                            <p v-if="accessLog">{{ accessLog.created_at | datetime }}</p>
                            <p v-else>{{ $t('Never') }}</p>
                        </div>
                    </div>
                    <div class="flex w-full">
                        <div class="px-4 py-2 w-1/2 sm:w-1/4">
                            <h4 class="font-normal text-gray-500 mt-2">{{ $t('Status') }}</h4>
                        </div>
                        <div class="px-4 py-4 w-1/2 sm:w-3/4">
                            <span
                                class="px-2 py-1 font-semibold leading-tight"
                                :class="{
                                    'text-green-700 bg-green-100': player.active,
                                    'text-red-700 bg-red-100': !player.active
                                }"
                            >
                              {{ player.active ?  $t('Active') :  $t('Inactive') }}
                            </span>
                        </div>
                    </div>
                    <div class="flex justify-center mt-6">
                        <btn
                            class="w-full"
                            v-if="!player.active"
                            @click="activate(player.id)"
                            :type="'button'"
                            :color="'green'"
                            :aria-label="$t('Activate')"
                            :title="$t('Activate')">
                            {{ $t('Activate') }}
                        </btn>
                        <btn
                            class="w-full"
                            v-if="player.active && newBalance <= 0"
                            @click="deactivate(player.id)"
                            :type="'button'"
                            :color="'red'"
                            :aria-label="$t('Deactivate')"
                            :title="$t('Deactivate')">
                            {{ $t('Deactivate') }}
                        </btn>
                    </div>
                </card>
            </div>
            <div v-if="withdrawalPlayerWithPinAllowed"
                class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/2">
                <card class="flex items-center">
                    <div class="p-3 mr-4 text-teal-500 bg-teal-100 rounded-full dark:text-teal-100 dark:bg-teal-500">
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clip-rule="evenodd"></path>
                        </svg>
                    </div>
                    <div v-if="requestedWithdrawals.length > 0">
                        <p class="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                            {{ $t('Open Withdrawal') }} ({{ this.requestedWithdrawals[0].created_at | datetime }})
                        </p>
                        <p class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                            {{ showFormattedAmount(this.requestedWithdrawals[0].amount, this.player.main_wallet.currency.code) }}
                        </p>
                    </div>
                    <div v-else>
                        <p>{{ $t('No open withdrawals') }}</p>
                    </div>
                    <div class="w-full flex justify-end" v-if="requestedWithdrawals.length > 0">
                        <btn v-if="player.type === 'terminal'" :processing="processing"  @click="acceptWithdrawal">
                            <span>{{ $t('Accept') }}</span>
                        </btn>
                        <btn-link v-if="requestedWithdrawals.length > 0" :href="route('withdrawals.show', requestedWithdrawals[0].id)">
                            <span>{{ $t('Accept') }}</span>
                        </btn-link>
                    </div>
                </card>
            </div>
            <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/2">
                <card class="flex items-center">
                    <div class="p-3 mr-4 text-green-500 bg-green-100 dark:text-green-100 dark:bg-green-500">
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"></path>
                        </svg>
                    </div>
                    <div>
                        <p class="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                            {{ $t('Current player\'s balance') }}
                        </p>
                        <p class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                            {{ showFormattedAmount(balance, this.player.main_wallet.currency.code) }}
                        </p>
                    </div>
                    <div class="w-full flex justify-end">
                        <div v-if="player.active">
                            <btn class="mr-2" :processing="processing || this.newBalance === 0" @click="withdrawAll" v-if="withdrawalPosWithoutPinAllowed">
                                <span>{{ $t('Withdraw') }}</span>
                            </btn>
                            <btn-link :href="route('players.deposit', player.id)">
                                <span>{{ $t('Deposit') }}</span>
                            </btn-link>
                        </div>
                        <span v-else class="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">{{ $t('Please activate the player before depositing') }}</span>
                    </div>
                </card>
            </div>
            <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full">
                <div class="mt-4 flex justify-end">
                    <h2 class="w-full leading-6 text-white font-bold text-2xl flex-no-shrink my-2">{{ $t('History') }}</h2>
                    <search-filter
                        v-model="form.search"
                        class="w-full max-w-md mb-6"
                        :disable-filter="true"
                        @reset="reset"
                    />
                </div>
                <div v-if="history.data.length > 0" class="w-full overflow-hidden shadow-xs">
                    <div class="w-full overflow-x-auto rounded-t">
                        <table class="w-full whitespace-no-wrap">
                            <thead>
                            <tr class="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
                                <th class="px-4 py-3 text-left">{{ $t('Created At') }}</th>
                                <th class="px-4 py-3 text-center">{{ $t('Game Name') }}</th>
                                <th class="px-4 py-3 text-center">{{ $t('Type') }}</th>
                                <th class="px-4 py-3 text-right">{{ $t('Amount') }}</th>
                            </tr>
                            </thead>
                            <tbody class="bg-gray-200 divide-y dark:divide-gray-700 dark:bg-gray-800">
                            <tr v-for="transaction in history.data" :key="transaction.id" class="text-gray-700 dark:text-gray-400">
                                <td class="px-4 py-3 text-left">{{ transaction.created_at | datetime }}</td>
                                <td class="px-4 py-3 text-center">{{ transaction.name }}</td>
                                <td class="px-4 py-3 text-center"><span
                                    class="px-2 py-1 font-semibold leading-tight"
                                    :class="{
                                                'text-green-700 bg-green-100': transaction.amount > 0,
                                                'text-red-700 bg-red-100': transaction.amount < 0
                                            }"
                                >
                                          {{ transaction.type }}
                                        </span>
                                </td>
                                <td class="px-4 py-3 text-right">{{ showFormattedAmount(transaction.amount, transaction.code) }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="flex justify-between px-4 py-3 text-md font-semibold tracking-wide text-gray-500 rounded-b uppercase border-t dark:border-gray-700 bg-gray-50 sm:grid-cols-9 dark:text-gray-400 dark:bg-gray-800">
                    <span class="flex items-center col-span-3">
                      {{ history.from }} - {{ history.to }} {{ $t('of') }} {{ history.total }}
                    </span>
                        <span class="col-span-2"></span>
                        <pagination :links="history.links" />
                    </div>
                </div>
                <div v-else class="flex flex-wrap justify-center text-2xl text-white">
                    {{ $t('No Entries') }}
                </div>
            </div>
            <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full text-right">
                <btn
                    @click="forcePlayerRefresh"
                    :type="'button'"
                    color="gray"
                    :aria-label="$t('Force reload')"
                    :title="$t('Force reload')">
                    {{$t('Force reload')}}
                </btn>
            </div>
        </div>
    </div>
</template>

<script>
import { debounce, pickBy, mapValues } from 'lodash';
import Pagination from '../../Components/Pagination';
import SearchFilter from '../../Components/SearchFilter';
import PageHeader from '../../Components/PageHeader';
import Card from '../../Components/Card';
import BackButton from '../../Components/BackButton';
import Btn from '../../Components/Btn';
import BtnLink from '../../Components/BtnLink';
import { Inertia } from '@inertiajs/inertia'
import printJS from "print-js";

export default {
    name: "Show",
    components: { Pagination, SearchFilter, BtnLink, Btn, PageHeader, Card, BackButton },
    props: {
        player: Object,
        balance: Number,
        accessLog: Object,
        filters: Object,
        history: Object,
        withdrawalsLocked: Boolean
    },
    metaInfo() {
        return {
            title: this.title
        }
    },
    computed: {
        title () {
            return `${this.$t('Details')} ${this.$t('for')} ${this.player.username}`;
        },
        confirm () {
            return this.$t('Are you sure you want to withdraw - ');
        }
    },
    data() {
        return {
            form: {
                search: this.filters.search
            },
            newBalance: 0,
            requestedWithdrawals: null,
            processing: false,
            withdrawalPlayerWithPinAllowed: window.withdrawal_player_with_pin,
            withdrawalPosWithoutPinAllowed: window.withdrawal_pos_without_pin,
        }
    },
    created() {
        this.newBalance = this.balance;
        this.requestedWithdrawals = this.player.requested_withdrawals;
        // this.hasBlockedWithdrawals();

        Echo
            .private(`operator-${this.$page.props.user.id}-player-${this.player.id}`)
            .listen('BalanceChanged', (e) => {
                this.newBalance = e.balance;
            })

        Echo
            .private(`operator-${this.$page.props.user.id}`)
            .listen('WithdrawalRequested', (e) => {
                if (this.player.id === e.playerId) {
                    axios.get(route('players.withdrawal', this.player.id).url()).then(response => {
                        this.requestedWithdrawals = response.data.withdrawal
                    });
                }
            })
            .listen('WithdrawalCanceled', (e) => {
                if (this.player.id === e.playerId) {
                    axios.get(route('players.withdrawal', this.player.id).url()).then(response => {
                        this.requestedWithdrawals = response.data.withdrawal
                    });
                }
            })
    },
    watch: {
        form: {
            handler: debounce(function() {
                let query = pickBy(this.form);
                query.player = this.player.id;
                this.$inertia.get(
                    this.route(
                        'players.show',
                        query
                    ), { preserveScroll: true }
                );
            }, 1000),
            deep: true,
        },
    },
    methods: {
        async deactivate(id) {
            this.$inertia.post(route('players.deactivate', id).url(), { preserveScroll: true });
        },
        async activate(id) {
            this.$inertia.post(route('players.activate', id).url(), { preserveScroll: true });
        },
        share() {
            const shareData = {
                title: this.player.username,
                url: route('first.login', this.player.hash).url(),
            }
            navigator.share(shareData)
        },
        isAbleToShare() {
            return location.protocol === 'https:';
        },
        reset() {
            this.form = mapValues(this.form, () => null);
        },
        refresh() {
            this.$inertia.reload();
        },
        async forcePlayerRefresh() {
            await confirm(this.$t('Are you sure you want to reload player page?'));
            axios.put(route('players.reload', this.player.id).url());
        },
        async withdrawAll() {
            if (this.balance === 0) {
                this.$page.props.flash.error = this.$t('Balance') + ' 0';
            } else {
                this.processing = true;
                await this.$inertia.post(route('player.withdraw-all', this.player.id).url(), { preserveScroll: true }, {
                    onBefore: () => confirm(this.$t('Withdraw all from player confirmation'))
                });
                this.$page.props.flash.success = this.$t('Withdrawal successful');
                this.processing = false;
            }
        },
        async acceptWithdrawal() {
            this.processing = true;
            await this.$inertia.put(route('withdrawals.process', this.player.requested_withdrawals[0].id).url(), {}, {
                onBefore: () => confirm(this.confirm + `${this.$options.filters.currency(this.newBalance)}?`)
            });
            this.processing = false;
        },
        async unlockWithdrawals() {
            await this.$inertia.post(route('player.unlock-withdrawals', this.player.id).url(), { preserveScroll: true }, {
                onBefore: () => confirm(this.$t('Withdrawal confirmation text'))
            });
        },
        printQr() {
            printJS({
                printable: "playerQr",
                type: "html",
            });
        },
        showFormattedAmount(amount, currencyCode) {
            return this.$formatCurrency(amount, currencyCode);
        }
    }
}
</script>

<style lang="scss" scoped>
    tr:nth-child(even) {
        background-color: #f4f5f7;
    }
    h1 {
        min-width: 50%;
    }
</style>
